import { reactive, readonly } from 'vue'
import { logInfo } from '@/helpers/Logger'
import sessionStore from '@/store/session'
import { FLOW_TYPE } from '@/config/constants'

// DATA
const consoleColor = 'color: blue'

const partnerStore = reactive({
  flowType: null,
  salesPartnerNumber: null,
  isPartnerFlow: false
})

const partner = readonly(partnerStore)

function partnerStoreFactory() {
  /**
   * Get flow type - delegates to sessionStore
   * @returns {string|null} - Flow type or null
   */
  function getFlowType() {
    if (sessionStore && typeof sessionStore.getFlowType === 'function') {
      return sessionStore.getFlowType()
    }
    return partnerStore.flowType
  }

  /**
   * Check if this is a partner flow - delegates to sessionStore
   * @returns {boolean} - Whether this is a partner flow
   */
  function isPartnerFlow() {
    if (sessionStore && typeof sessionStore.isPartnerFlow === 'function') {
      return sessionStore.isPartnerFlow()
    }
    return partnerStore.isPartnerFlow
  }

    /**
   * Set sales partner number
   * @param {string} salesPartnerNumber - Sales partner number to set
   * @param {string} flowType - Flow type to determine if this is a valid partner flow
   */
  function setSalesPartnerData(salesPartnerNumber, flowType = null) {
    // Update sessionStore as the source of truth
    if (sessionStore) {
      sessionStore.setFlowType(flowType)
      sessionStore.setPartnerFlow(flowType === FLOW_TYPE.EMPLOYEE_FLOW)
      sessionStore.setSalesPartnerNumber(salesPartnerNumber)
    }
    
    // Update local store to match sessionStore
    partnerStore.salesPartnerNumber = sessionStore?.getSalesPartnerNumber()
    partnerStore.isPartnerFlow = sessionStore?.getFlowType()
    
    logInfo(['%cPARTNER SET', consoleColor, salesPartnerNumber, 'isPartnerFlow:', partnerStore.isPartnerFlow])
  }

  /**
   * Get sales partner number - delegates to sessionStore
   * @returns {string|null} - Sales partner number or null
   */
  function getSalesPartnerNumber() {
    if (sessionStore && typeof sessionStore.getSalesPartnerNumber === 'function') {
      return sessionStore.getSalesPartnerNumber()
    }
    return partnerStore.salesPartnerNumber
  }

  /**
   * Clear partner data - delegates to sessionStore
   */
  function clearPartner() {
    // Clear from sessionStore first
    if (sessionStore && typeof sessionStore.setPartnerFlow === 'function') {
      sessionStore.setFlowType(null)
      sessionStore.setPartnerFlow(false)
      sessionStore.setSalesPartnerNumber(null)
    }
    
    // Update local store to match
    partnerStore.flowType = null
    partnerStore.isPartnerFlow = false
    partnerStore.salesPartnerNumber = null
    
    logInfo(['%cPARTNER CLEARED', consoleColor])
  }

  /**
   * Initialize from session store (for persistence across navigation)
   */
  function initializeFromSession() {
    // Sync local store with sessionStore state
    if (sessionStore) {
      partnerStore.flowType = sessionStore.getFlowType() || null
      partnerStore.isPartnerFlow = sessionStore.isPartnerFlow()
      partnerStore.salesPartnerNumber = sessionStore.getSalesPartnerNumber() || null
      
      if (partnerStore.isPartnerFlow) {
        logInfo(['%cPARTNER STATE RESTORED', consoleColor, 'From session', 'salesPartnerNumber:', partnerStore.salesPartnerNumber])
      }
    }
  }
  
  return {
    partner,
    getFlowType,
    isPartnerFlow,
    setSalesPartnerData,
    getSalesPartnerNumber,
    clearPartner,
    initializeFromSession,
  }
}

export default partnerStoreFactory()
